import React, { useEffect, useRef, useState } from 'react';

interface ParallaxContainerProps {
  children: React.ReactNode;
  speed?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  className?: string;
  offset?: number;
}

export const ParallaxContainer: React.FC<ParallaxContainerProps> = ({
  children,
  speed = 0.5,
  direction = 'up',
  className = '',
  offset = 0
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [elementTop, setElementTop] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const updateElementPosition = () => {
      const rect = element.getBoundingClientRect();
      setElementTop(rect.top + window.scrollY);
    };

    updateElementPosition();
    window.addEventListener('resize', updateElementPosition);

    return () => {
      window.removeEventListener('resize', updateElementPosition);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      // Check if element is in viewport
      const element = elementRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      setIsInView(
        rect.bottom >= 0 && 
        rect.top <= windowHeight
      );
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getTransform = () => {
    if (!isInView) return 'none';

    const scrollProgress = (scrollY - elementTop + offset) * speed;
    
    switch (direction) {
      case 'up':
        return `translateY(${-scrollProgress}px)`;
      case 'down':
        return `translateY(${scrollProgress}px)`;
      case 'left':
        return `translateX(${-scrollProgress}px)`;
      case 'right':
        return `translateX(${scrollProgress}px)`;
      default:
        return `translateY(${-scrollProgress}px)`;
    }
  };

  return (
    <div
      ref={elementRef}
      className={`${className}`}
      style={{
        transform: getTransform(),
        transition: 'none',
        willChange: 'transform'
      }}
    >
      {children}
    </div>
  );
};

// Component for multiple parallax layers
interface ParallaxLayersProps {
  layers: Array<{
    content: React.ReactNode;
    speed: number;
    direction?: 'up' | 'down' | 'left' | 'right';
    className?: string;
  }>;
  className?: string;
}

export const ParallaxLayers: React.FC<ParallaxLayersProps> = ({
  layers,
  className = ''
}) => {
  return (
    <div className={`relative ${className}`}>
      {layers.map((layer, index) => (
        <ParallaxContainer
          key={index}
          speed={layer.speed}
          direction={layer.direction}
          className={`absolute inset-0 ${layer.className || ''}`}
        >
          {layer.content}
        </ParallaxContainer>
      ))}
    </div>
  );
};